import { defineStore } from "pinia";
import { ref, reactive, computed, watch, inject } from "vue";
import { useFetch } from "@/helpers/composables/useFetch";
import { router } from "@inertiajs/vue3";
import { useDebounceFn, useStorage } from '@vueuse/core';
import { useLists } from "@/helpers/composables/useList";
import { useSettingsStore } from "@/store/settings.ts";




export const useCheckoutD2CStore = ()=>{
    return defineStore("checkoutD2CStore", ()=>{

        let route = inject('route');
        let { applyCouponOnList, fetchRestrictions } = useLists({ router, route });
        const settingsStore = useSettingsStore();
        let { setFreeShippingByCoupon, setListTotalAfterDiscount } = settingsStore;

        const couponCode = useStorage("coupon",null);
        const couponInformation = ref({});
    
        // TAX
        const taxPayload = ref({
            zip: null,
            list_id: null,
            shipping: null,
            address1: null,
            address2: null,
            city: null,
            state: null,
            used_coupon: null,
            email: null,
        })
        const setTaxPayload = (form)=>{
            taxPayload.value = {
                zip: form?.shipping_zip,
                list_id: form?.list_id,
                shipping: form?.shipping_option?.cost ?? 0,
                address1: form?.shipping_address,
                address2: form?.shipping_address2,
                city: form?.shipping_city,
                state: form?.shipping_state,
                used_coupon: form?.used_coupon,
                email: form?.email,
            }
        }

        const { 
            data:taxData, 
            execute:executeGetTax,
            isFetching: isLoadingTax,
        } = useFetch(route('get.tax'), {
            initialData: {"taxAmount": 0, "status": false, message: null},
            immediate: false,
            beforeFetch: (ctx) => {
                ctx.options.headers = {
                    ...ctx.options.headers,
                    'Accept': 'application/json',
                }
                return ctx;
            },
            afterFetch: async (ctx) => {

            },
            onError: () => {

            }
        }).post(taxPayload).json();

        const fetchTax = useDebounceFn(()=>{
            executeGetTax();
        }, 500);

        const recalculateTax = ()=>{
            // console.log("GET TAX")
            fetchTax();
        };

        const recalculateTaxAwaitable = async ()=>{
            await executeGetTax();
        };

        const taxAmount = computed(()=>{
            return taxData.value.taxAmount;
        })
        const taxErrorMessage = computed(()=>{
            return !taxData.value.status && taxData.value.message ? taxData.value.message : null;
        })

        // SHIPPING RATES
        const shippingStateForRates = ref(null);
        const restrictions = ref([]);

        const shippingRatesPayload = computed(()=>({
            used_coupon: couponCode.value,
            state: shippingStateForRates?.value ?? null
        }))
        const isLoadingShippingRates = ref(false);
        const isLoadingRestrictions = ref(false);
        const isUpdatingCartItem = ref(false);
        const { 
            data:shippingRates, 
            execute:executeGetShippingRates
        } = useFetch(route('update.shipping'), {
            immediate: false,
            beforeFetch: (ctx) => {
                ctx.options.headers = {
                    ...ctx.options.headers,
                    'Accept': 'application/json',
                }
                return ctx;
            },
            afterFetch: async (ctx) => {
                isLoadingShippingRates.value = false;
            },
            onError: () => {
                isLoadingShippingRates.value = false;
            }
        }).post(shippingRatesPayload).json();

        const fetchShippingRates = useDebounceFn(()=>{
            executeGetShippingRates();
        }, 500);

        const getShippingRates = ()=>{
            // console.log("GET SHIPPING RATES");
            isLoadingShippingRates.value = true;
            fetchShippingRates();
        };

        const checkRestrictions = (payload)=>{
            if(!payload?.order_skus?.length) {
                return;
            }

            isLoadingRestrictions.value = true;
            fetchRestrictions(payload, {
                afterFetch: (ctx) => {
                    restrictions.value = ctx.data;
                    isLoadingRestrictions.value = false;
                },
                onFetchError: ()=>{
                    isLoadingRestrictions.value = false;
                }
            });
        };

        const checkRestrictionsAwaitable = (payload)=>{
            return new Promise((resolve, reject) => {
                if(!payload?.order_skus?.length) {
                    resolve(null);
                    return;
                }

                isLoadingRestrictions.value = true;
                fetchRestrictions(payload, {
                    afterFetch: (ctx) => {
                        restrictions.value = ctx.data;
                        isLoadingRestrictions.value = false;
                        resolve(ctx.data);
                    },
                    onFetchError: (ctx)=>{
                        isLoadingRestrictions.value = false;
                        reject(ctx);
                    }
                });
            });
        };

        // COUPON
        const applyCouponAndRecalculate = (couponCodeInput, skipRecalculation=false)=>{
            // console.log("APPLY COUPON", couponCodeInput)
            if(!['',null,undefined].includes(couponCodeInput?.trim())){
                applyCouponOnList(couponCodeInput,{
                    afterFetch: (ctx)=>{
                        couponInformation.value = ctx.data;
                        if(couponInformation.value.applied){
                            couponCode.value = couponCodeInput;
                            if(!skipRecalculation){
                                recalculateTax();
                                getShippingRates();
                            }
                        }

                        setListTotalAfterDiscount(couponInformation.value?.listAmount);
                        setFreeShippingByCoupon(couponInformation.value?.freeShipping);

                    },
                    onFetchError: ()=>{
                        couponInformation.value = {
                            applied: false,
                            message: "There was an issue applying this coupon. Please try again."
                        }
                        
                    }
                })
            }
        }
        const reapplyCoupon = ()=>{
            // console.log("REAPPLY")
            applyCouponAndRecalculate(couponCode.value, true);
        }

        const hasShippingAddress = computed(()=>{
            return ['address1','city','state','zip'].every(field=>{
                return !['',null,undefined].includes(taxPayload.value[field]);
            })
        })

        // ORDER
        const reloadOrderInfo = ()=>{
            // console.log("RELOAD ORDER INFO")
            router.reload({ 
                only: [
                    'checkoutResource',
                    'current_list',
                    'user'
                ],
            })
        }

        const isLoadingCalculation = computed(()=>{
            return isLoadingTax.value || isLoadingShippingRates.value || isLoadingRestrictions.value;
        })

        const clearCoupon = ()=>{
            couponCode.value = null;
        }

        const reset = ()=>{
            taxPayload.value = {};
            couponInformation.value = {};
            couponCode.value = null;
            
            setFreeShippingByCoupon(false);
            setListTotalAfterDiscount(null);
        }
    
        return {
            shippingStateForRates,
            taxPayload,
            setTaxPayload,
            taxAmount,
            taxErrorMessage,
            couponInformation,
            recalculateTax,
            recalculateTaxAwaitable,
            getShippingRates,
            shippingRates,
            applyCouponAndRecalculate,
            reapplyCoupon,
            reloadOrderInfo,
            isLoadingCalculation,
            hasShippingAddress,
            reset,
            clearCoupon,
            checkRestrictions,
            checkRestrictionsAwaitable,
            restrictions,
            isUpdatingCartItem
        }
    })();
}
